import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { rolesApi } from '@/services/rest/apiInstances';
import { UserRole } from '@/api/client';

export const fetchRoles = createAsyncThunk('roles/fetchRoles', async () => {
    try {
        const data = await rolesApi.listRoles();
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export const fetchClientRoles = createAsyncThunk(
    'roles/fetchClientRoles',
    async (clientId: any) => {
        try {
            const data = await rolesApi.listRoles({ headers: { 'X-Client-Id': clientId } });
            return data.data;
        } catch (error: any) {
            return error.data;
        }
    },
);

export const getCurrentUserRoles = createAsyncThunk('roles/getCurrentUserRoles', async () => {
    try {
        const data = await rolesApi.listUserRoles();
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export const getUserPermissions = createAsyncThunk('roles/getUserPermissions', async () => {
    try {
        const data = await rolesApi.listUserPermissions();
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export interface IRolesState {
    isPending: boolean;
    rolesMap: UserRole[];
    clientRolesMap: UserRole[];
    errors?: any;
    uerRoles: UserRole[];
    isUserAdmin: boolean;
    userPermissions: any[];
    isOpenSelectClientModal: boolean;
}

const initialState: IRolesState = {
    isPending: true,
    rolesMap: [],
    clientRolesMap: [],
    errors: undefined,
    uerRoles: [],
    isUserAdmin: false,
    userPermissions: [],
    isOpenSelectClientModal: false,
};

export const rolesSlice = createSlice({
    name: 'roles',
    initialState,
    reducers: {
        setPending: (state, action: PayloadAction<{ isPending: boolean }>) => {
            state.isPending = action.payload.isPending;
        },
        setIsOpenSelectClientModal: (state, action: PayloadAction<{ isOpen: boolean }>) => {
            state.isOpenSelectClientModal = action.payload.isOpen;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRoles.pending, (state) => {
                state.rolesMap = [];
            })
            .addCase(fetchRoles.rejected, (state, action) => {
                state.errors = action.error;
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                const rolesList = action.payload;

                if (rolesList) {
                    state.rolesMap = rolesList;
                }
            })
            .addCase(fetchClientRoles.pending, (state) => {
                state.clientRolesMap = [];
            })
            .addCase(fetchClientRoles.rejected, (state, action) => {
                state.errors = action.error;
            })
            .addCase(fetchClientRoles.fulfilled, (state, action) => {
                const rolesList = action.payload;

                if (rolesList) {
                    state.clientRolesMap = rolesList;
                }
            })
            .addCase(getCurrentUserRoles.pending, (state) => {
                state.uerRoles = [];
                state.isUserAdmin = false;
            })
            .addCase(getCurrentUserRoles.rejected, (state, action) => {
                state.errors = action.error;
            })
            .addCase(getCurrentUserRoles.fulfilled, (state, action) => {
                const rolesList = action.payload;
                const findAdminRoleInUserRoleList = (rolesList || []).find(
                    (role: UserRole) => role.name === 'Admin',
                );
                if (findAdminRoleInUserRoleList) {
                    state.isUserAdmin = true;
                }

                if (rolesList) {
                    state.uerRoles = rolesList;
                }
            })
            .addCase(getUserPermissions.pending, (state) => {
                state.isPending = true;
                state.userPermissions = [];
            })
            .addCase(getUserPermissions.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(getUserPermissions.fulfilled, (state, action) => {
                const userPermissions = action.payload;

                state.isPending = false;
                if (userPermissions) {
                    state.userPermissions = userPermissions;
                }
            });
    },
});

export const { setPending, setIsOpenSelectClientModal } = rolesSlice.actions;

export default rolesSlice.reducer;
